import { useEffect, useState } from 'react';
import { atHomeAccount, atHomeAccountAction } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { useApi } from '../overview/common';
import type { OpCtx } from './OperatorHome';
import { stateStyle } from './OpMembers';
import { Act, D, S } from './ui';

// Member management (design T11643-11679, hAdmin*): account, devices, care circle,
// onboarding, billing. Reads the PROPOSED GET /athome/members/:id/account and writes the
// PROPOSED POST /athome/members/:id/account-actions. Anything that locks a person out or
// deletes something (ban, close, unpair) asks first and sends confirm:true.

type Tone = 'plain' | 'warn' | 'danger';
interface RowDef { key: string; k: string; label: string; doneLabel: string; tone: Tone; back?: string }
const GROUPS: { name: string; note: (dev: string) => string; rows: RowDef[] }[] = [
  { name: 'Account', note: () => 'sign-in and access', rows: [
    { key: 'pw', k: 'Password', label: 'Send reset', doneLabel: 'reset link sent', tone: 'plain' },
    { key: 'signout', k: 'Active sessions', label: 'Sign out all', doneLabel: 'signed out everywhere', tone: 'plain' },
    { key: 'suspend', k: 'Service', label: 'Suspend', doneLabel: 'suspended · alerts still route', tone: 'warn', back: 'Resume' },
    { key: 'ban', k: 'Access', label: 'Ban', doneLabel: 'banned', tone: 'danger', back: 'Reinstate' },
    { key: 'close', k: 'Account', label: 'Close account', doneLabel: 'closing · 30-day wipe', tone: 'danger', back: 'Cancel close' },
  ] },
  { name: 'Devices', note: (dev) => dev, rows: [
    { key: 'test', k: 'Test the device', label: 'Force test', doneLabel: 'test sent', tone: 'plain' },
    { key: 'lock', k: 'Lock', label: 'Lock', doneLabel: 'locked', tone: 'warn', back: 'Unlock' },
    { key: 'replace', k: 'Replacement', label: 'Ship one', doneLabel: 'shipping', tone: 'plain' },
    { key: 'unpair', k: 'Pairing', label: 'Unpair', doneLabel: 'unpaired', tone: 'danger', back: 'Re-pair' },
  ] },
  { name: 'Care circle', note: () => 'who can see and decide', rows: [
    { key: 'invite', k: 'Family', label: 'Invite', doneLabel: 'invite sent', tone: 'plain' },
    { key: 'revoke', k: 'Revoke a login', label: 'Revoke', doneLabel: 'revoked', tone: 'warn', back: 'Restore' },
    { key: 'transfer', k: 'Approver', label: 'Transfer', doneLabel: 'transferred', tone: 'plain' },
  ] },
  { name: 'Onboarding', note: () => 'first 90 days', rows: [
    { key: 'resend', k: 'Setup', label: 'Resend guide', doneLabel: 'guide sent', tone: 'plain' },
    { key: 'call', k: 'Welcome call', label: 'Book', doneLabel: 'booked', tone: 'plain' },
  ] },
  { name: 'Billing', note: () => 'consumer subscription', rows: [
    { key: 'plan', k: 'Plan', label: 'Change', doneLabel: 'changed', tone: 'plain' },
    { key: 'refund', k: 'Refund', label: 'Refund a month', doneLabel: 'refunded', tone: 'warn' },
  ] },
];
const btn = (tone: Tone, done: boolean) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;height:30px;min-width:88px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;' +
  (done ? 'color:#54606C;background:rgba(34,39,46,.07);' : tone === 'danger' ? 'color:#B3261E;border:1px solid rgba(179,38,30,.4);' : tone === 'warn' ? 'color:#4A5866;border:1px solid rgba(74,88,102,.4);' : 'color:#2F5C97;border:1px solid rgba(47,92,151,.35);');

export function AdminDrawer({ c, residentId, onClose }: { c: OpCtx; residentId: string; onClose: () => void }) {
  const { client } = useSession();
  const acct = useApi((cl, signal) => atHomeAccount(cl, { params: { id: residentId }, signal }), [residentId]);
  const [doneText, setDoneText] = useState<string | null>(null);
  const [ask, setAsk] = useState<{ row: RowDef; undo: boolean } | null>(null);
  const member = c.o.caseload.data?.members.find((m) => m.residentId === residentId);
  const a = acct.data;
  const name = a?.name ?? member?.name ?? '';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !ask) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, ask]);

  const send = async (row: RowDef, undo: boolean, confirm = false) => {
    const r = await atHomeAccountAction(client, { params: { id: residentId }, body: { action: row.key, undo, confirm } });
    setDoneText(r.done);
    acct.reload();
    c.o.caseload.reload();
  };
  const press = (row: RowDef, undo: boolean) => {
    if (c.readOnly) return;
    if (row.tone === 'danger' && !undo) {
      setAsk({ row, undo });
      return;
    }
    send(row, undo).catch((e: Error) => setDoneText(`Not sent: ${e.message}.`));
  };

  const state = a?.state ?? member?.account?.state ?? '–';
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', justifyContent: 'flex-end', background: 'rgba(34,39,46,.42)' }}>
      <a href="#" aria-label="Close" onClick={(e) => { e.preventDefault(); onClose(); }} style={{ position: 'absolute', inset: 0 }} />
      <div role="dialog" aria-modal="true" aria-label={`Member management: ${name}`} data-testid="admin-drawer" style={{ position: 'relative', boxSizing: 'border-box', width: 'min(460px,100%)', height: '100%', overflow: 'auto', background: '#FCFAF4', borderLeft: '1px solid rgba(34,39,46,.16)', boxShadow: '-12px 0 40px rgba(34,39,46,.2)', padding: '16px 18px 24px' }}>
        <D css="display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;">
          <S css="flex:1 1 0;min-width:0;">
            <S css="display:block;font-size:14px;font-weight:700;letter-spacing:.1em;color:#54606C;">MEMBER MANAGEMENT</S>
            <S css="display:block;font-size:19px;font-weight:700;letter-spacing:-.02em;color:#22272E;margin-top:2px;">{name}</S>
            <S css="display:block;font-size:14px;color:#54606C;margin-top:2px;">{a?.meta ?? (member ? `${member.devicesReporting} of ${member.devicesLinked} devices reporting` : '')}</S>
          </S>
          <S css={stateStyle(state).replace('height:17px;padding:0 7px;border-radius:4px;', 'height:20px;padding:0 8px;border-radius:5px;').replace('letter-spacing:.06em', 'letter-spacing:.07em')}>{state}</S>
          <Act css="font-size:14px;color:#54606C;text-decoration:none;padding:6px;" onClick={onClose}>
            Close
          </Act>
        </D>
        {doneText && (
          <D css="display:flex;align-items:center;gap:9px;margin-top:12px;padding:9px 11px;border-radius:9px;background:rgba(47,138,111,.12);border:1px solid rgba(47,138,111,.32);" role="status">
            <S css="font-size:16px;color:#1F6B52;">{'✓'}</S>
            <S css="flex:1 1 0;min-width:0;font-size:14px;color:#12503C;line-height:1.4;text-wrap:pretty;">{doneText}</S>
          </D>
        )}
        {!a && acct.error && (
          <D css="margin-top:14px;font-size:14px;color:#54606C;line-height:1.4;">Account controls are not connected yet: waiting on GET /athome/members/:id/account. Nothing here can be changed until they are.</D>
        )}
        {a &&
          GROUPS.map((g) => (
            <D key={g.name} css="margin-top:14px;padding-top:11px;border-top:1px solid rgba(34,39,46,.10);min-width:0;">
              <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:6px;">
                <S css="font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;">{g.name}</S>
                <S css="flex:1 1 120px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;text-wrap:pretty;">{g.note(`${a.deviceName} · ${a.deviceNote}`)}</S>
              </D>
              {g.rows.map((row) => {
                const cur = a.rows[row.key] ?? { value: '–', done: false };
                const done = cur.done;
                return (
                  <D key={row.key} css="display:flex;align-items:center;gap:10px;padding:5px 0;min-width:0;">
                    <S css="flex:1 1 0;min-width:0;">
                      <S css="display:block;font-size:16px;font-weight:600;color:#22272E;">{row.k}</S>
                      <S css="display:block;font-size:14px;color:#54606C;line-height:1.35;text-wrap:pretty;">{done ? row.doneLabel : cur.value}</S>
                    </S>
                    <Act
                      css={btn(done && row.back ? 'plain' : row.tone, done && !row.back)}
                      onClick={() => (done ? row.back && press(row, true) : press(row, false))}
                      disabled={c.readOnly || (done && !row.back)}
                      reason={c.readOnly ? c.reason : 'Already done.'}
                    >
                      {done ? row.back ?? 'Done' : row.label}
                    </Act>
                  </D>
                );
              })}
            </D>
          ))}
        <D css="font-size:14px;color:#54606C;line-height:1.4;margin-top:14px;padding-top:10px;border-top:1px solid rgba(34,39,46,.10);text-wrap:pretty;">
          Every action here is written to the ledger with your name and the time. Suspending pauses billing and Atlas but keeps alerts routing; banning stops both. Closing an account starts a 30-day wipe you can cancel.
        </D>
      </div>
      {ask && (
        <ConfirmDialog
          title={`${ask.row.label}: ${name}?`}
          body={ask.row.key === 'ban' ? 'Sign-in is blocked for the member and the whole care circle until you reinstate them.' : ask.row.key === 'close' ? 'This starts a 30-day wipe of the account. You can cancel it until then.' : 'Nothing is monitored until a device is paired again.'}
          confirmLabel={ask.row.label}
          danger
          onConfirm={() => send(ask.row, ask.undo, true)}
          onClose={() => setAsk(null)}
        />
      )}
    </div>
  );
}
